'use client';

import { useCallback, useEffect, useState } from 'react';
import { getDb } from '@/lib/db';
import { STORAGE_UNAVAILABLE_MESSAGE, storageErrorMessage } from '@/lib/storageErrors';
import { Tag } from '@/lib/types';

export type TagPayload = Omit<Tag, 'id' | 'createdAt' | 'updatedAt'>;

function notifyUpdate() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('budget-db-update'));
  }
}

function createId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function useTags() {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const list = await db.tags.orderBy('name').toArray();
      setTags(list);
      setError(null);
    } catch (err) {
      setError(storageErrorMessage('Impossible de charger les tags.', err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener('budget-db-update', handler);
    return () => window.removeEventListener('budget-db-update', handler);
  }, [load]);

  const addTag = useCallback(async (payload: TagPayload) => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      return null;
    }

    try {
      const now = new Date().toISOString();
      const tag = {
        ...payload,
        id: createId(),
        createdAt: now,
        updatedAt: now,
      };
      await db.tags.add(tag);
      setError(null);
      notifyUpdate();
      return tag;
    } catch (err) {
      setError(storageErrorMessage("Impossible d'ajouter le tag.", err));
      return null;
    }
  }, []);

  const updateTag = useCallback(async (id: string, payload: TagPayload) => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      return;
    }

    try {
      await db.tags.update(id, { ...payload, updatedAt: new Date().toISOString() });
      setError(null);
      notifyUpdate();
    } catch (err) {
      setError(storageErrorMessage('Impossible de modifier le tag.', err));
    }
  }, []);

  const deleteTag = useCallback(async (id: string) => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      return;
    }

    try {
      await db.tags.delete(id);
      setError(null);
      notifyUpdate();
    } catch (err) {
      setError(storageErrorMessage('Impossible de supprimer le tag.', err));
    }
  }, []);

  const resetData = useCallback(async () => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      return;
    }

    try {
      await db.tags.clear();
      setError(null);
      notifyUpdate();
    } catch (err) {
      setError(storageErrorMessage('Impossible de réinitialiser les tags.', err));
    }
  }, []);

  return {
    tags,
    loading,
    error,
    addTag,
    updateTag,
    deleteTag,
    resetData,
  };
}
